import { computed, ref } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";
import { ActionTypes } from "@/store/actions";
import { undoActionStack } from "@/canvas-api/ActionStack";
import { canvasReactive } from "@/canvas-api/Canvas";
import { hideIcons, showIcons } from "@/canvas-api/ObjectIcons";
import { clearClonedObject } from "@/canvas-api/ObjectUtility";
import { currentText, currentFont, currentFontSize } from "@/canvas-api/UserObjects/Text";
import { handleSnap, setAllLinesOpaque } from "@/canvas-api/GridLines";
import { Transform } from "@/types";

export const opacity = ref(0);
export const strokeWidth = ref(0);
export const fillElement = ref<HTMLElement>();
export const borderElement = ref<HTMLElement>();
export const borderWidth = ref(0);

export const borderWidthRange = ref<HTMLInputElement>();
export  const transparencyRange = ref<HTMLInputElement>();

const showGrid = computed<boolean>(() => store.getters.settingsState.grid);
const isRotated = computed<boolean>(() =>
  store.getters.canvasOrientation === "rotated" ? true : false
);

export const attachEventListeners = (canvas: fabric.Canvas) => {
  textChanged(canvas);
  objectActionStack(canvas);
  selectionCreated(canvas); //FIRES ON SET ACTIVE OBJECT CANVAS METHOD
  selectionUpdated(canvas);
  selectionCleared(canvas);
  mouseDown(canvas);
  objectMoving(canvas);
  objectRotating(canvas);
  objectScaling(canvas);
  objectModified(canvas);
};

function verifyEvent(target?: any): target is fabric.Object {
  return target !== undefined && target !== null && target.id > 0;
}

const textChanged = (canvas: fabric.Canvas) => {
  canvas.on("text:changed", (event: fabric.IEvent): void => {
    const target = event.target as fabric.IText;
    if (target && target.text) {
      currentText.value = target.text;
    }
  });
};

const objectActionStack = (canvas: fabric.Canvas) => {
  canvas.on("object:actionStack", (event: fabric.IEvent): void => {
    const target = event.target as fabric.Object;

    if (verifyEvent(target)) {
      showIcons(target);
    }

    canvas.renderAll();
  });
};

const selectionCreated = (canvas: fabric.Canvas) => {
  canvas.on("selection:created", (event: fabric.IEvent): void => {
    if (event.target && event.target.name && event.target.name.includes("objectIcon")) {
      return;
    }

    try {
      if (verifyEvent(event.target)) {
        showIcons(event.target);
        event.target.showAngleDisplayControl(false);

        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, event.target);

        switch (event.target.name) {
          case "svgShape": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "Shapes",
              key: 0,
            });
            break;
          }
          case "iText": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "Text",
              key: 0,
            });
            break;
          }
          case "clipArt": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "ClipArt",
              key: 0,
            });
            break;
          }
          case "image": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "Image",
              key: 0,
            });
            break;
          }
        }
      } else {
        throw new Error(`Expected fabric event, got '${event.target}'. selection:created`);
      }
    } catch (e) {
      console.log(e);
    }
  });
};

const selectionUpdated = (canvas: fabric.Canvas) => {
  canvas.on("selection:updated", (event: fabric.IEvent): void => {
    if (event.target && event.target.name && event.target.name.includes("objectIcon")) {
      return;
    }

    try {
      if (verifyEvent(event.target)) {
        showIcons(event.target);
        event.target.showAngleDisplayControl(false);

        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, event.target);

        //This triggers the side tool bar to rerender
        //https://michaelnthiessen.com/force-re-render/
        let key = store.getters.accordianState.key;
        switch (event.target.name) {
          case "svgShape": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "Shapes",
              key: key += 1,
            });
            break;
          }
          case "iText": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "Text",
              key: key += 1,
            });
    
            const activeTextObjext = event.target as fabric.IText;
  
            currentText.value = activeTextObjext.text!;
            currentFont.value = activeTextObjext.fontFamily!;
            currentFontSize.value = activeTextObjext.fontSize! * activeTextObjext.scaleX!;
  
            const fontWeight = document.getElementById("fontWeight") as HTMLInputElement;
            if (fontWeight && activeTextObjext.fontWeight === "bold") {
              fontWeight.checked = true;
            } else if (fontWeight) {
              fontWeight.checked = false;
            }
  
            const fontItalic = document.getElementById("fontStyle") as HTMLInputElement;
            if (fontItalic && activeTextObjext.fontStyle === "italic") {
              fontItalic.checked = true;
            } else if (fontItalic){
              fontItalic.checked = false ;
            }
  
            const underLine = document.getElementById("underline") as HTMLInputElement;
            if (underLine && activeTextObjext.underline) {
              underLine.checked = true;
            } else if (underLine) {
              underLine.checked = false;
            }
  
            const textAlignInput = document.getElementById(
              `${activeTextObjext.textAlign}Align`
            ) as HTMLInputElement;
    
            if (textAlignInput) {
              textAlignInput.checked = true;
            }
  
            if (borderElement.value) {
              borderElement.value.style.background = activeTextObjext.stroke as string;
            }
            
            if (fillElement.value) {
              fillElement.value.style.background = activeTextObjext.fill as string;
            }
          
            strokeWidth.value = activeTextObjext.strokeWidth! * 10;
            opacity.value = 100 - activeTextObjext.opacity! * 100;
            
            break;
          }
          case "clipArt": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "ClipArt",
              key: key += 1,
            });
            break;
          }
          case "image": {
            store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
              isOpen: true,
              tool: "Image",
              key: key += 1,
            });
            break;
          }
        }

        if (event.target.name === "svgShape" || event.target.name === "clipArt") {          
          const activeObject = event.target;

          const isGroup = (obj: fabric.Object): obj is fabric.Group => {
            const tmp = obj as fabric.Group;
    
            if (tmp._objects) {
              return true;
            } else {
              return false;
            }
          };

          if (isGroup(activeObject)) {            
            const activeObjStrokeWidth = (activeObject._objects[0].strokeWidth as number) * 10;
            const activeObjOpacity = 100 - (activeObject.opacity as number) * 100;
    
            if (borderWidthRange.value && transparencyRange.value) {
              borderWidthRange.value.value = activeObjStrokeWidth.toString();
              transparencyRange.value.value = activeObjOpacity.toString();
            }
    
            borderWidth.value = activeObjStrokeWidth;
            opacity.value = activeObjOpacity;
    
            if (fillElement.value && activeObject.fill) {
              fillElement.value.style.background = activeObject._objects[0].fill as string;
            }
    
            if (borderElement.value && activeObject.stroke) {
              borderElement.value.style.background = activeObject._objects[0].stroke as string;
            }
          } else {
            const activeObjStrokeWidth = (activeObject.strokeWidth as number) * 10;
            const activeObjOpacity = 100 - (activeObject.opacity as number) * 100;

            if (borderWidthRange.value && transparencyRange.value) {
              borderWidthRange.value.value = activeObjStrokeWidth.toString();
              transparencyRange.value.value = activeObjOpacity.toString();
            }
  
            borderWidth.value = activeObjStrokeWidth;
            opacity.value = activeObjOpacity;
  
            if (fillElement.value && activeObject.fill) {
              fillElement.value.style.background = activeObject.fill as string;
            }
  
            if (borderElement.value && activeObject.stroke) {
              borderElement.value.style.background = activeObject.stroke as string;
            }
          }
        }
        clearClonedObject();
      } else {
        throw new Error(`Expected fabric event, got '${event.target}'. selection:updated`);
      }
    } catch (e) {
      console.log(e);
    }
  });
};

const selectionCleared = (canvas: fabric.Canvas) => {
  canvas.on("selection:cleared", (): void => {
    hideIcons();
  });
};

const mouseDown = (canvas: fabric.Canvas) => {
  canvas.on("mouse:down", (event: fabric.IEvent): void => {
    if (event.target && event.target.name === "objectIconArrange") {
      return;
    } else {
      if (canvasReactive.setShowStackPlacement) canvasReactive.setShowStackPlacement(false);
    }

    if (!event.target || (event.target && event.target.name === "objectIconDelete")) {
      hideIcons();
      clearClonedObject();

      store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
        key: 0,
        isOpen: false,
        tool: "",
      });
      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, undefined);
    }
  });
};

const objectMoving = (canvas: fabric.Canvas) => {
  canvas.on("object:moving", (event: fabric.IEvent): void => {
    try {
      if (verifyEvent(event.target)) {
        hideIcons();

        const fabricEvent = event.target;

        if (showGrid.value) {
          handleSnap(fabricEvent);
        }
      } else {
        throw new Error(`Expected fabric event, got '${event}'. object:moving`);
      }
    } catch (e) {
      console.log(e);
    }
  });
};

const objectRotating = (canvas: fabric.Canvas) => {
  try {
    canvas.on("object:rotating", (event: fabric.IEvent): void => {
      if (verifyEvent(event.target) && event.target.angle !== undefined) {
        event.target.showAngleDisplayControl(true);
        hideIcons();
      } else {
        throw new Error(`Expected fabric event, got '${event}'. object:rotating`);
      }
    });
  } catch (e) {
    console.log(e);
  }
};

const objectScaling = (canvas: fabric.Canvas) => {
  try {
    canvas.on("object:scaling", (event: fabric.IEvent): void => {
      if (verifyEvent(event.target)) {
        hideIcons();
      } else {
        throw new Error(`Expected fabric event, got '${event}'. object:scaling`);
      }
    });
  } catch (e) {
    console.log(e);
  }
};

const objectModified = (canvas: fabric.Canvas) => {
  canvas.on("object:modified", (event: fabric.IEvent): void => {
    if (
      !event.transform ||
      (event.target && event.target.name && event.target.name.includes("objectIcon"))
    ) {
      return;
    }

    const transform = event.transform as any;
    const originalTransform = {
      ...transform.original,
      action: transform.action,
    } as Transform;

    const target = event.target as fabric.Object;

    try {
      if (verifyEvent(target) && originalTransform.action) {
        showIcons(target);
        switch (originalTransform.action) {
          //Align is a custom made object:modified action created in useObjectUtility
          case "align": {
            break;
          }
          case "drag": {
            undoActionStack.push({
              id: target.id as number,
              type: "drag",
              transform: originalTransform,
            });

            setAllLinesOpaque(canvas);
            break;
          }
          case "rotate": {
            target.showAngleDisplayControl(false);
            undoActionStack.push({
              id: target.id as number,
              type: "rotate",
              transform: originalTransform,
            });
            break;
          }
          case "scaleX":
          case "scaleY":
          case "scale": {
            undoActionStack.push({
              id: target.id as number,
              type: "scale",
              transform: originalTransform,
            });

            //Rerender EditImage component after image is scaled to update the Width/Height inputs
            if (target.name === "image") {
              let key = store.getters.accordianState.key;
              store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
                ...store.getters.accordianState,
                key: key += 1,
              });
            } else if (target.name === "iText") {
              const text = target as fabric.IText;
              if (text.scaleX) {
                currentFontSize.value = Number((text.fontSize! * text.scaleX!).toFixed(1));
              }
            }
            break;
          }
        }
      } else {
        throw new Error(`Expected fabric event, got '${event}'. object:modified`);
      }
    } catch (e) {
      console.log(e);
    }
  });
};
